import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getDCAForHRRules, getSubDCAByDCA, getCostCenterStatesList, getAllEmpCostCenters,
    getAllEmpGroups, saveNewEmpGroup,
    getAllEmpCategories, saveNewEmpCategory, updateEmpCategory,
    getGroupForSalaryRule, getAllSalaryHeads, getSalaryHeadType, saveNewSalaryHead,
    getAllStaffSalryHeads, getCostCenterStatesForMinWage, getAllMinWageRule,
    saveHRSalaryRules, deleteHRSalaryHeadMinWage,
    getPFESIRuleData, saveHRPFESIRule,
    getAllHolidays, saveNewHoliday, deleteHoliday,
    getEmpLeaveTypes, getLeaveRuleYears, getGroupsPaidLeaves,
    checkCustomLeaveExistForPageload, checkCustomLeaveExist,
    getAllLPaidLeavesData, saveELorPaidLeaveRule, deleteELPaidLeave,
    getGratuityRuleData, saveHRGratuityRule,
    getGroupsForAdvance, getAllAdvanceRuleData, saveHRAdvanceRule, deleteAdvanceRule,
    getGroupsForNoticePeriod, getNotificationToRole, getNoticePeriodData,
    saveHRNoticePeriodRule, deleteNoticePeriod,
    getGroupsForTaxRule, getStatesListForTax, getProfessionalTaxData, saveHRTaxRule, deleteProfessionalTax,
    getGroupsForTravelRule, getAllHRTravelExpRules, saveHRTravelRule, deleteHRTravelExpRule,
} from '../../api/HRAPI/hrRulesAPI';

const err = (e, fallback) => e?.Message || e?.message || fallback;

// ── Shared lookups ───────────────────────────────────────────────────────────

export const fetchDCAForHRRules = createAsyncThunk('hrRules/fetchDCA', async (_, { rejectWithValue }) => {
    try { const res = await getDCAForHRRules(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch DCA list')); }
});

export const fetchSubDCAByDCACode = createAsyncThunk('hrRules/fetchSubDCA', async (dcaCode, { rejectWithValue }) => {
    try { const res = await getSubDCAByDCA(dcaCode); return { dcaCode, list: res?.Data || [] }; }
    catch (e) { return rejectWithValue({ dcaCode, message: err(e, 'Failed to fetch Sub DCA list') }); }
});

export const fetchStatesList = createAsyncThunk('hrRules/fetchStatesList', async (_, { rejectWithValue }) => {
    try { const res = await getCostCenterStatesList(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch states')); }
});

export const fetchEmpCostCenters = createAsyncThunk('hrRules/fetchEmpCostCenters', async (_, { rejectWithValue }) => {
    try { const res = await getAllEmpCostCenters(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch cost centres')); }
});

// ── 1. Groups ────────────────────────────────────────────────────────────────

export const fetchAllEmpGroups = createAsyncThunk('hrRules/fetchAllEmpGroups', async (_, { rejectWithValue }) => {
    try { const res = await getAllEmpGroups(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch groups')); }
});

export const submitEmpGroup = createAsyncThunk('hrRules/submitEmpGroup', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveNewEmpGroup(payload);
        if (res?.Data !== 'Submited') return rejectWithValue(res?.Data || 'Failed to save group');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save group')); }
});

// ── 2. Employee Category ─────────────────────────────────────────────────────

export const fetchAllEmpCategories = createAsyncThunk('hrRules/fetchAllEmpCategories', async (_, { rejectWithValue }) => {
    try { const res = await getAllEmpCategories(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch employee categories')); }
});

export const submitNewEmpCategory = createAsyncThunk('hrRules/submitNewEmpCategory', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveNewEmpCategory(payload);
        if (res?.Data !== 'Submited') return rejectWithValue(res?.Data || 'Failed to save category');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save category')); }
});

export const submitUpdateEmpCategory = createAsyncThunk('hrRules/submitUpdateEmpCategory', async (payload, { rejectWithValue }) => {
    try {
        const res = await updateEmpCategory(payload);
        if (res?.Data !== 'Submited') return rejectWithValue(res?.Data || 'Failed to update category');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to update category')); }
});

// ── 3. Salary / Min Wages ─────────────────────────────────────────────────────

export const fetchGroupForSalaryRule = createAsyncThunk('hrRules/fetchGroupForSalaryRule', async (_, { rejectWithValue }) => {
    try { const res = await getGroupForSalaryRule(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch groups')); }
});

export const fetchAllSalaryHeads = createAsyncThunk('hrRules/fetchAllSalaryHeads', async (heads = 'None', { rejectWithValue }) => {
    try { const res = await getAllSalaryHeads(heads); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch salary heads')); }
});

export const fetchSalaryHeadType = createAsyncThunk('hrRules/fetchSalaryHeadType', async (headname, { rejectWithValue }) => {
    try { const res = await getSalaryHeadType(headname); return res?.Data ?? ''; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch head type')); }
});

export const submitNewSalaryHead = createAsyncThunk('hrRules/submitNewSalaryHead', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveNewSalaryHead(payload);
        if (res?.Data !== 'Submited') return rejectWithValue(res?.Data || 'Failed to save salary head');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save salary head')); }
});

export const fetchAllStaffSalryHeads = createAsyncThunk('hrRules/fetchAllStaffSalryHeads', async (_, { rejectWithValue }) => {
    try { const res = await getAllStaffSalryHeads(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch salary head rules')); }
});

export const fetchCostCenterStatesForMinWage = createAsyncThunk('hrRules/fetchCostCenterStatesForMinWage', async (skillType, { rejectWithValue }) => {
    try { const res = await getCostCenterStatesForMinWage(skillType); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch states')); }
});

export const fetchAllMinWageRule = createAsyncThunk('hrRules/fetchAllMinWageRule', async (_, { rejectWithValue }) => {
    try { const res = await getAllMinWageRule(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch minimum wage rules')); }
});

/** payload.StaffType: 'Staff' | 'Labour' */
export const submitSalaryRule = createAsyncThunk('hrRules/submitSalaryRule', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveHRSalaryRules(payload);
        if (res?.Data !== 'Submited') return rejectWithValue(res?.Data || 'Failed to save salary rule');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save salary rule')); }
});

export const submitDeleteSalaryRule = createAsyncThunk('hrRules/submitDeleteSalaryRule', async (payload, { rejectWithValue }) => {
    try {
        const res = await deleteHRSalaryHeadMinWage(payload);
        if (res?.Data !== 'Deleted') return rejectWithValue(res?.Data || 'Failed to delete salary rule');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to delete salary rule')); }
});

// ── 4. PF & ESI ───────────────────────────────────────────────────────────────

export const fetchPFESIRuleData = createAsyncThunk('hrRules/fetchPFESIRuleData', async (_, { rejectWithValue }) => {
    try { const res = await getPFESIRuleData(); return res?.Data || null; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch PF/ESI rule')); }
});

export const submitPFESIRule = createAsyncThunk('hrRules/submitPFESIRule', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveHRPFESIRule(payload);
        if (res?.Data !== 'Submited') return rejectWithValue(res?.Data || 'Failed to save PF/ESI rule');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save PF/ESI rule')); }
});

// ── 5. Holidays ───────────────────────────────────────────────────────────────

export const fetchAllHolidays = createAsyncThunk('hrRules/fetchAllHolidays', async (_, { rejectWithValue }) => {
    try { const res = await getAllHolidays(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch holidays')); }
});

export const submitNewHoliday = createAsyncThunk('hrRules/submitNewHoliday', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveNewHoliday(payload);
        if (res?.Data !== 'Submited') return rejectWithValue(res?.Data || 'Failed to save holiday');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save holiday')); }
});

export const submitDeleteHoliday = createAsyncThunk('hrRules/submitDeleteHoliday', async (payload, { rejectWithValue }) => {
    try {
        const res = await deleteHoliday(payload);
        if (res?.Data !== 'Deleted') return rejectWithValue(res?.Data || 'Failed to delete holiday');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to delete holiday')); }
});

// ── 6. EL / Paid Leave ────────────────────────────────────────────────────────

export const fetchEmpLeaveTypes = createAsyncThunk('hrRules/fetchEmpLeaveTypes', async (_, { rejectWithValue }) => {
    try { const res = await getEmpLeaveTypes(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch leave types')); }
});

export const fetchLeaveRuleYears = createAsyncThunk('hrRules/fetchLeaveRuleYears', async (_, { rejectWithValue }) => {
    try { const res = await getLeaveRuleYears(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch leave rule years')); }
});

export const fetchGroupsPaidLeaves = createAsyncThunk('hrRules/fetchGroupsPaidLeaves', async (params, { rejectWithValue }) => {
    try { const res = await getGroupsPaidLeaves(params); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch groups')); }
});

export const fetchCheckCustomLeaveExistForPageload = createAsyncThunk('hrRules/fetchCheckCustomLeaveExistForPageload', async (_, { rejectWithValue }) => {
    try { const res = await checkCustomLeaveExistForPageload(); return res?.Data || null; }
    catch (e) { return rejectWithValue(err(e, 'Failed to check existing EL/Paid leave rule')); }
});

export const fetchCheckCustomLeaveExist = createAsyncThunk('hrRules/fetchCheckCustomLeaveExist', async (params, { rejectWithValue }) => {
    try { const res = await checkCustomLeaveExist(params); return res?.Data?.ExistMsg ?? res?.Data ?? ''; }
    catch (e) { return rejectWithValue(err(e, 'Failed to validate EL/Paid leave rule')); }
});

export const fetchAllLPaidLeavesData = createAsyncThunk('hrRules/fetchAllLPaidLeavesData', async (_, { rejectWithValue }) => {
    try { const res = await getAllLPaidLeavesData(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch EL/Paid leave rules')); }
});

export const submitELorPaidLeaveRule = createAsyncThunk('hrRules/submitELorPaidLeaveRule', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveELorPaidLeaveRule(payload);
        if (res?.Data !== 'Submited') return rejectWithValue(res?.Data || 'Failed to save EL/Paid leave rule');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save EL/Paid leave rule')); }
});

export const submitDeleteELPaidLeave = createAsyncThunk('hrRules/submitDeleteELPaidLeave', async (payload, { rejectWithValue }) => {
    try {
        const res = await deleteELPaidLeave(payload);
        if (res?.Data !== 'Deleted') return rejectWithValue(res?.Data || 'Failed to delete EL/Paid leave rule');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to delete EL/Paid leave rule')); }
});

// ── 7. Gratuity ───────────────────────────────────────────────────────────────

export const fetchGratuityRuleData = createAsyncThunk('hrRules/fetchGratuityRuleData', async (_, { rejectWithValue }) => {
    try { const res = await getGratuityRuleData(); return res?.Data || null; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch gratuity rule')); }
});

export const submitGratuityRule = createAsyncThunk('hrRules/submitGratuityRule', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveHRGratuityRule(payload);
        if (res?.Data !== 'Submited') return rejectWithValue(res?.Data || 'Failed to save gratuity rule');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save gratuity rule')); }
});

// ── 8. Advance ────────────────────────────────────────────────────────────────

export const fetchGroupsForAdvance = createAsyncThunk('hrRules/fetchGroupsForAdvance', async (_, { rejectWithValue }) => {
    try { const res = await getGroupsForAdvance(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch groups')); }
});

export const fetchAllAdvanceRuleData = createAsyncThunk('hrRules/fetchAllAdvanceRuleData', async (status, { rejectWithValue }) => {
    try { const res = await getAllAdvanceRuleData(status); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch advance rules')); }
});

export const submitAdvanceRule = createAsyncThunk('hrRules/submitAdvanceRule', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveHRAdvanceRule(payload);
        if (res?.Data !== 'Submited') return rejectWithValue(res?.Data || 'Failed to save advance rule');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save advance rule')); }
});

export const submitDeleteAdvanceRule = createAsyncThunk('hrRules/submitDeleteAdvanceRule', async (payload, { rejectWithValue }) => {
    try {
        const res = await deleteAdvanceRule(payload);
        if (res?.Data !== 'Deleted') return rejectWithValue(res?.Data || 'Failed to delete advance rule');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to delete advance rule')); }
});

// ── 9. Notice Period ──────────────────────────────────────────────────────────

export const fetchGroupsForNoticePeriod = createAsyncThunk('hrRules/fetchGroupsForNoticePeriod', async (_, { rejectWithValue }) => {
    try { const res = await getGroupsForNoticePeriod(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch groups')); }
});

export const fetchNotificationToRole = createAsyncThunk('hrRules/fetchNotificationToRole', async (_, { rejectWithValue }) => {
    try { const res = await getNotificationToRole(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch notification roles')); }
});

export const fetchNoticePeriodData = createAsyncThunk('hrRules/fetchNoticePeriodData', async (_, { rejectWithValue }) => {
    try { const res = await getNoticePeriodData(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch notice period rules')); }
});

export const submitNoticePeriodRule = createAsyncThunk('hrRules/submitNoticePeriodRule', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveHRNoticePeriodRule(payload);
        if (res?.Data !== 'Submited') return rejectWithValue(res?.Data || 'Failed to save notice period rule');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save notice period rule')); }
});

export const submitDeleteNoticePeriod = createAsyncThunk('hrRules/submitDeleteNoticePeriod', async (payload, { rejectWithValue }) => {
    try {
        const res = await deleteNoticePeriod(payload);
        if (res?.Data !== 'Deleted') return rejectWithValue(res?.Data || 'Failed to delete notice period rule');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to delete notice period rule')); }
});

// ── 10. Tax ───────────────────────────────────────────────────────────────────

export const fetchGroupsForTaxRule = createAsyncThunk('hrRules/fetchGroupsForTaxRule', async (_, { rejectWithValue }) => {
    try { const res = await getGroupsForTaxRule(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch groups')); }
});

export const fetchStatesListForTax = createAsyncThunk('hrRules/fetchStatesListForTax', async (groupId, { rejectWithValue }) => {
    try { const res = await getStatesListForTax(groupId); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch states')); }
});

export const fetchProfessionalTaxData = createAsyncThunk('hrRules/fetchProfessionalTaxData', async (_, { rejectWithValue }) => {
    try { const res = await getProfessionalTaxData(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch tax rules')); }
});

export const submitTaxRule = createAsyncThunk('hrRules/submitTaxRule', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveHRTaxRule(payload);
        if (res?.Data !== 'Submited') return rejectWithValue(res?.Data || 'Failed to save tax rule');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save tax rule')); }
});

export const submitDeleteTaxRule = createAsyncThunk('hrRules/submitDeleteTaxRule', async (payload, { rejectWithValue }) => {
    try {
        const res = await deleteProfessionalTax(payload);
        if (res?.Data !== 'Deleted') return rejectWithValue(res?.Data || 'Failed to delete tax rule');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to delete tax rule')); }
});

// ── 11. Official Travel ───────────────────────────────────────────────────────

export const fetchGroupsForTravelRule = createAsyncThunk('hrRules/fetchGroupsForTravelRule', async (_, { rejectWithValue }) => {
    try { const res = await getGroupsForTravelRule(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch groups')); }
});

export const fetchAllHRTravelExpRules = createAsyncThunk('hrRules/fetchAllHRTravelExpRules', async (type, { rejectWithValue }) => {
    try { const res = await getAllHRTravelExpRules(type); return res?.Data || { TravelExpenseslst: [], GridType: type }; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch travel expense rules')); }
});

export const submitTravelRule = createAsyncThunk('hrRules/submitTravelRule', async (payload, { rejectWithValue }) => {
    try {
        const res = await saveHRTravelRule(payload);
        if (res?.Data !== 'Submited') return rejectWithValue(res?.Data || 'Failed to save travel expense rule');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to save travel expense rule')); }
});

export const submitDeleteTravelRule = createAsyncThunk('hrRules/submitDeleteTravelRule', async (payload, { rejectWithValue }) => {
    try {
        const res = await deleteHRTravelExpRule(payload);
        if (res?.Data !== 'Deleted') return rejectWithValue(res?.Data || 'Failed to delete travel expense rule');
        return res.Data;
    } catch (e) { return rejectWithValue(err(e, 'Failed to delete travel expense rule')); }
});

// ── Slice ──────────────────────────────────────────────────────────────────────

const initialState = {
    // shared lookups
    dcaList: [], dcaLoading: false,
    subDcaByDca: {}, subDcaLoadingByDca: {},
    statesList: [], statesLoading: false,
    empCostCenters: [], empCostCentersLoading: false,

    // groups
    groups: [], groupsLoading: false, groupsError: null,
    groupSaveLoading: false, groupSaveResult: null, groupSaveError: null,

    // categories
    categories: [], categoriesLoading: false, categoriesError: null,
    categorySaveLoading: false, categorySaveResult: null, categorySaveError: null,

    // Salary / Min Wages
    salaryGroups: [], salaryGroupsLoading: false,
    salaryHeadsMaster: [], salaryHeadsMasterLoading: false,
    salaryHeadType: '', salaryHeadTypeLoading: false,
    newSalaryHeadSaveLoading: false, newSalaryHeadSaveResult: null, newSalaryHeadSaveError: null,
    staffSalaryHeadRules: [], staffSalaryHeadRulesLoading: false, staffSalaryHeadRulesError: null,
    minWageStates: [], minWageStatesLoading: false,
    minWageRules: [], minWageRulesLoading: false, minWageRulesError: null,
    salaryRuleSaveLoading: false, salaryRuleSaveResult: null, salaryRuleSaveError: null,

    // PF & ESI
    pfesiData: null, pfesiLoading: false, pfesiError: null,
    pfesiSaveLoading: false, pfesiSaveResult: null, pfesiSaveError: null,

    // holidays
    holidays: [], holidaysLoading: false, holidaysError: null,
    holidaySaveLoading: false, holidaySaveResult: null, holidaySaveError: null,

    // EL / Paid Leave
    leaveTypes: [], leaveTypesLoading: false,
    leaveYears: [], leaveYearsLoading: false,
    paidLeaveGroups: [], paidLeaveGroupsLoading: false,
    existingElPaidLeave: null, existingElPaidLeaveLoading: false,
    leaveExistCheck: null, leaveExistCheckLoading: false,
    elPaidLeaves: [], elPaidLeavesLoading: false, elPaidLeavesError: null,
    elPaidLeaveSaveLoading: false, elPaidLeaveSaveResult: null, elPaidLeaveSaveError: null,

    // gratuity
    gratuityData: null, gratuityLoading: false, gratuityError: null,
    gratuitySaveLoading: false, gratuitySaveResult: null, gratuitySaveError: null,

    // advance
    advanceGroups: [], advanceGroupsLoading: false,
    advanceRules: [], advanceRulesLoading: false, advanceRulesError: null,
    advanceSaveLoading: false, advanceSaveResult: null, advanceSaveError: null,

    // notice period
    noticePeriodGroups: [], noticePeriodGroupsLoading: false,
    notificationRoles: [], notificationRolesLoading: false,
    noticePeriodData: [], noticePeriodDataLoading: false, noticePeriodDataError: null,
    noticePeriodSaveLoading: false, noticePeriodSaveResult: null, noticePeriodSaveError: null,

    // tax
    taxGroups: [], taxGroupsLoading: false,
    taxStates: [], taxStatesLoading: false,
    taxData: [], taxDataLoading: false, taxDataError: null,
    taxSaveLoading: false, taxSaveResult: null, taxSaveError: null,

    // travel
    travelGroups: [], travelGroupsLoading: false,
    travelData: [], travelGridType: 'CurrentlyActive', travelDataLoading: false, travelDataError: null,
    travelSaveLoading: false, travelSaveResult: null, travelSaveError: null,
};

const hrRulesSlice = createSlice({
    name: 'hrRules',
    initialState,
    reducers: {
        clearGroupSaveResult:        (s) => { s.groupSaveResult = null; s.groupSaveError = null; },
        clearCategorySaveResult:     (s) => { s.categorySaveResult = null; s.categorySaveError = null; },
        clearSalaryRuleSaveResult:   (s) => { s.salaryRuleSaveResult = null; s.salaryRuleSaveError = null; },
        clearNewSalaryHeadSaveResult:(s) => { s.newSalaryHeadSaveResult = null; s.newSalaryHeadSaveError = null; },
        clearPFESISaveResult:        (s) => { s.pfesiSaveResult = null; s.pfesiSaveError = null; },
        clearHolidaySaveResult:      (s) => { s.holidaySaveResult = null; s.holidaySaveError = null; },
        clearELPaidLeaveSaveResult:  (s) => { s.elPaidLeaveSaveResult = null; s.elPaidLeaveSaveError = null; },
        clearLeaveExistCheck:        (s) => { s.leaveExistCheck = null; },
        clearGratuitySaveResult:     (s) => { s.gratuitySaveResult = null; s.gratuitySaveError = null; },
        clearAdvanceSaveResult:      (s) => { s.advanceSaveResult = null; s.advanceSaveError = null; },
        clearNoticePeriodSaveResult: (s) => { s.noticePeriodSaveResult = null; s.noticePeriodSaveError = null; },
        clearTaxSaveResult:          (s) => { s.taxSaveResult = null; s.taxSaveError = null; },
        clearTravelSaveResult:       (s) => { s.travelSaveResult = null; s.travelSaveError = null; },
        resetHRRules: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDCAForHRRules.pending,   (s) => { s.dcaLoading = true; })
            .addCase(fetchDCAForHRRules.fulfilled, (s, a) => { s.dcaLoading = false; s.dcaList = a.payload; })
            .addCase(fetchDCAForHRRules.rejected,  (s) => { s.dcaLoading = false; });

        builder
            .addCase(fetchSubDCAByDCACode.pending,   (s, a) => { s.subDcaLoadingByDca[a.meta.arg] = true; })
            .addCase(fetchSubDCAByDCACode.fulfilled, (s, a) => { s.subDcaLoadingByDca[a.payload.dcaCode] = false; s.subDcaByDca[a.payload.dcaCode] = a.payload.list; })
            .addCase(fetchSubDCAByDCACode.rejected,  (s, a) => { s.subDcaLoadingByDca[a.meta.arg] = false; });

        builder
            .addCase(fetchStatesList.pending,   (s) => { s.statesLoading = true; })
            .addCase(fetchStatesList.fulfilled, (s, a) => { s.statesLoading = false; s.statesList = a.payload; })
            .addCase(fetchStatesList.rejected,  (s) => { s.statesLoading = false; });

        builder
            .addCase(fetchEmpCostCenters.pending,   (s) => { s.empCostCentersLoading = true; })
            .addCase(fetchEmpCostCenters.fulfilled, (s, a) => { s.empCostCentersLoading = false; s.empCostCenters = a.payload; })
            .addCase(fetchEmpCostCenters.rejected,  (s) => { s.empCostCentersLoading = false; });

        // Groups
        builder
            .addCase(fetchAllEmpGroups.pending,   (s) => { s.groupsLoading = true; s.groupsError = null; })
            .addCase(fetchAllEmpGroups.fulfilled, (s, a) => { s.groupsLoading = false; s.groups = a.payload; })
            .addCase(fetchAllEmpGroups.rejected,  (s, a) => { s.groupsLoading = false; s.groupsError = a.payload; });
        builder
            .addCase(submitEmpGroup.pending,   (s) => { s.groupSaveLoading = true; s.groupSaveError = null; s.groupSaveResult = null; })
            .addCase(submitEmpGroup.fulfilled, (s, a) => { s.groupSaveLoading = false; s.groupSaveResult = a.payload; })
            .addCase(submitEmpGroup.rejected,  (s, a) => { s.groupSaveLoading = false; s.groupSaveError = a.payload; });

        // Categories
        builder
            .addCase(fetchAllEmpCategories.pending,   (s) => { s.categoriesLoading = true; s.categoriesError = null; })
            .addCase(fetchAllEmpCategories.fulfilled, (s, a) => { s.categoriesLoading = false; s.categories = a.payload; })
            .addCase(fetchAllEmpCategories.rejected,  (s, a) => { s.categoriesLoading = false; s.categoriesError = a.payload; });
        [submitNewEmpCategory, submitUpdateEmpCategory].forEach((thunk) => {
            builder
                .addCase(thunk.pending,   (s) => { s.categorySaveLoading = true; s.categorySaveError = null; s.categorySaveResult = null; })
                .addCase(thunk.fulfilled, (s, a) => { s.categorySaveLoading = false; s.categorySaveResult = a.payload; })
                .addCase(thunk.rejected,  (s, a) => { s.categorySaveLoading = false; s.categorySaveError = a.payload; });
        });

        // Salary / Min Wages
        builder
            .addCase(fetchGroupForSalaryRule.pending,   (s) => { s.salaryGroupsLoading = true; })
            .addCase(fetchGroupForSalaryRule.fulfilled, (s, a) => { s.salaryGroupsLoading = false; s.salaryGroups = a.payload; })
            .addCase(fetchGroupForSalaryRule.rejected,  (s) => { s.salaryGroupsLoading = false; });
        builder
            .addCase(fetchAllSalaryHeads.pending,   (s) => { s.salaryHeadsMasterLoading = true; })
            .addCase(fetchAllSalaryHeads.fulfilled, (s, a) => { s.salaryHeadsMasterLoading = false; s.salaryHeadsMaster = a.payload; })
            .addCase(fetchAllSalaryHeads.rejected,  (s) => { s.salaryHeadsMasterLoading = false; });
        builder
            .addCase(fetchSalaryHeadType.pending,   (s) => { s.salaryHeadTypeLoading = true; })
            .addCase(fetchSalaryHeadType.fulfilled, (s, a) => { s.salaryHeadTypeLoading = false; s.salaryHeadType = a.payload; })
            .addCase(fetchSalaryHeadType.rejected,  (s) => { s.salaryHeadTypeLoading = false; s.salaryHeadType = ''; });
        builder
            .addCase(submitNewSalaryHead.pending,   (s) => { s.newSalaryHeadSaveLoading = true; s.newSalaryHeadSaveError = null; s.newSalaryHeadSaveResult = null; })
            .addCase(submitNewSalaryHead.fulfilled, (s, a) => { s.newSalaryHeadSaveLoading = false; s.newSalaryHeadSaveResult = a.payload; })
            .addCase(submitNewSalaryHead.rejected,  (s, a) => { s.newSalaryHeadSaveLoading = false; s.newSalaryHeadSaveError = a.payload; });
        builder
            .addCase(fetchAllStaffSalryHeads.pending,   (s) => { s.staffSalaryHeadRulesLoading = true; s.staffSalaryHeadRulesError = null; })
            .addCase(fetchAllStaffSalryHeads.fulfilled, (s, a) => { s.staffSalaryHeadRulesLoading = false; s.staffSalaryHeadRules = a.payload; })
            .addCase(fetchAllStaffSalryHeads.rejected,  (s, a) => { s.staffSalaryHeadRulesLoading = false; s.staffSalaryHeadRulesError = a.payload; });
        builder
            .addCase(fetchCostCenterStatesForMinWage.pending,   (s) => { s.minWageStatesLoading = true; })
            .addCase(fetchCostCenterStatesForMinWage.fulfilled, (s, a) => { s.minWageStatesLoading = false; s.minWageStates = a.payload; })
            .addCase(fetchCostCenterStatesForMinWage.rejected,  (s) => { s.minWageStatesLoading = false; });
        builder
            .addCase(fetchAllMinWageRule.pending,   (s) => { s.minWageRulesLoading = true; s.minWageRulesError = null; })
            .addCase(fetchAllMinWageRule.fulfilled, (s, a) => { s.minWageRulesLoading = false; s.minWageRules = a.payload; })
            .addCase(fetchAllMinWageRule.rejected,  (s, a) => { s.minWageRulesLoading = false; s.minWageRulesError = a.payload; });
        [submitSalaryRule, submitDeleteSalaryRule].forEach((thunk) => {
            builder
                .addCase(thunk.pending,   (s) => { s.salaryRuleSaveLoading = true; s.salaryRuleSaveError = null; s.salaryRuleSaveResult = null; })
                .addCase(thunk.fulfilled, (s, a) => { s.salaryRuleSaveLoading = false; s.salaryRuleSaveResult = a.payload; })
                .addCase(thunk.rejected,  (s, a) => { s.salaryRuleSaveLoading = false; s.salaryRuleSaveError = a.payload; });
        });

        // PF & ESI
        builder
            .addCase(fetchPFESIRuleData.pending,   (s) => { s.pfesiLoading = true; s.pfesiError = null; })
            .addCase(fetchPFESIRuleData.fulfilled, (s, a) => { s.pfesiLoading = false; s.pfesiData = a.payload; })
            .addCase(fetchPFESIRuleData.rejected,  (s, a) => { s.pfesiLoading = false; s.pfesiError = a.payload; });
        builder
            .addCase(submitPFESIRule.pending,   (s) => { s.pfesiSaveLoading = true; s.pfesiSaveError = null; s.pfesiSaveResult = null; })
            .addCase(submitPFESIRule.fulfilled, (s, a) => { s.pfesiSaveLoading = false; s.pfesiSaveResult = a.payload; })
            .addCase(submitPFESIRule.rejected,  (s, a) => { s.pfesiSaveLoading = false; s.pfesiSaveError = a.payload; });

        // Holidays
        builder
            .addCase(fetchAllHolidays.pending,   (s) => { s.holidaysLoading = true; s.holidaysError = null; })
            .addCase(fetchAllHolidays.fulfilled, (s, a) => { s.holidaysLoading = false; s.holidays = a.payload; })
            .addCase(fetchAllHolidays.rejected,  (s, a) => { s.holidaysLoading = false; s.holidaysError = a.payload; });
        [submitNewHoliday, submitDeleteHoliday].forEach((thunk) => {
            builder
                .addCase(thunk.pending,   (s) => { s.holidaySaveLoading = true; s.holidaySaveError = null; s.holidaySaveResult = null; })
                .addCase(thunk.fulfilled, (s, a) => { s.holidaySaveLoading = false; s.holidaySaveResult = a.payload; })
                .addCase(thunk.rejected,  (s, a) => { s.holidaySaveLoading = false; s.holidaySaveError = a.payload; });
        });

        // EL / Paid Leave
        builder
            .addCase(fetchEmpLeaveTypes.fulfilled, (s, a) => { s.leaveTypes = a.payload; })
            .addCase(fetchLeaveRuleYears.fulfilled, (s, a) => { s.leaveYears = a.payload; });
        builder
            .addCase(fetchGroupsPaidLeaves.pending,   (s) => { s.paidLeaveGroupsLoading = true; })
            .addCase(fetchGroupsPaidLeaves.fulfilled, (s, a) => { s.paidLeaveGroupsLoading = false; s.paidLeaveGroups = a.payload; })
            .addCase(fetchGroupsPaidLeaves.rejected,  (s) => { s.paidLeaveGroupsLoading = false; });
        builder
            .addCase(fetchCheckCustomLeaveExistForPageload.pending,   (s) => { s.existingElPaidLeaveLoading = true; })
            .addCase(fetchCheckCustomLeaveExistForPageload.fulfilled, (s, a) => { s.existingElPaidLeaveLoading = false; s.existingElPaidLeave = a.payload; })
            .addCase(fetchCheckCustomLeaveExistForPageload.rejected,  (s) => { s.existingElPaidLeaveLoading = false; });
        builder
            .addCase(fetchCheckCustomLeaveExist.pending,   (s) => { s.leaveExistCheckLoading = true; })
            .addCase(fetchCheckCustomLeaveExist.fulfilled, (s, a) => { s.leaveExistCheckLoading = false; s.leaveExistCheck = a.payload; })
            .addCase(fetchCheckCustomLeaveExist.rejected,  (s) => { s.leaveExistCheckLoading = false; });
        builder
            .addCase(fetchAllLPaidLeavesData.pending,   (s) => { s.elPaidLeavesLoading = true; s.elPaidLeavesError = null; })
            .addCase(fetchAllLPaidLeavesData.fulfilled, (s, a) => { s.elPaidLeavesLoading = false; s.elPaidLeaves = a.payload; })
            .addCase(fetchAllLPaidLeavesData.rejected,  (s, a) => { s.elPaidLeavesLoading = false; s.elPaidLeavesError = a.payload; });
        [submitELorPaidLeaveRule, submitDeleteELPaidLeave].forEach((thunk) => {
            builder
                .addCase(thunk.pending,   (s) => { s.elPaidLeaveSaveLoading = true; s.elPaidLeaveSaveError = null; s.elPaidLeaveSaveResult = null; })
                .addCase(thunk.fulfilled, (s, a) => { s.elPaidLeaveSaveLoading = false; s.elPaidLeaveSaveResult = a.payload; })
                .addCase(thunk.rejected,  (s, a) => { s.elPaidLeaveSaveLoading = false; s.elPaidLeaveSaveError = a.payload; });
        });

        // Gratuity
        builder
            .addCase(fetchGratuityRuleData.pending,   (s) => { s.gratuityLoading = true; s.gratuityError = null; })
            .addCase(fetchGratuityRuleData.fulfilled, (s, a) => { s.gratuityLoading = false; s.gratuityData = a.payload; })
            .addCase(fetchGratuityRuleData.rejected,  (s, a) => { s.gratuityLoading = false; s.gratuityError = a.payload; });
        builder
            .addCase(submitGratuityRule.pending,   (s) => { s.gratuitySaveLoading = true; s.gratuitySaveError = null; s.gratuitySaveResult = null; })
            .addCase(submitGratuityRule.fulfilled, (s, a) => { s.gratuitySaveLoading = false; s.gratuitySaveResult = a.payload; })
            .addCase(submitGratuityRule.rejected,  (s, a) => { s.gratuitySaveLoading = false; s.gratuitySaveError = a.payload; });

        // Advance
        builder
            .addCase(fetchGroupsForAdvance.pending,   (s) => { s.advanceGroupsLoading = true; })
            .addCase(fetchGroupsForAdvance.fulfilled, (s, a) => { s.advanceGroupsLoading = false; s.advanceGroups = a.payload; })
            .addCase(fetchGroupsForAdvance.rejected,  (s) => { s.advanceGroupsLoading = false; });
        builder
            .addCase(fetchAllAdvanceRuleData.pending,   (s) => { s.advanceRulesLoading = true; s.advanceRulesError = null; })
            .addCase(fetchAllAdvanceRuleData.fulfilled, (s, a) => { s.advanceRulesLoading = false; s.advanceRules = a.payload; })
            .addCase(fetchAllAdvanceRuleData.rejected,  (s, a) => { s.advanceRulesLoading = false; s.advanceRulesError = a.payload; });
        [submitAdvanceRule, submitDeleteAdvanceRule].forEach((thunk) => {
            builder
                .addCase(thunk.pending,   (s) => { s.advanceSaveLoading = true; s.advanceSaveError = null; s.advanceSaveResult = null; })
                .addCase(thunk.fulfilled, (s, a) => { s.advanceSaveLoading = false; s.advanceSaveResult = a.payload; })
                .addCase(thunk.rejected,  (s, a) => { s.advanceSaveLoading = false; s.advanceSaveError = a.payload; });
        });

        // Notice Period
        builder
            .addCase(fetchGroupsForNoticePeriod.pending,   (s) => { s.noticePeriodGroupsLoading = true; })
            .addCase(fetchGroupsForNoticePeriod.fulfilled, (s, a) => { s.noticePeriodGroupsLoading = false; s.noticePeriodGroups = a.payload; })
            .addCase(fetchGroupsForNoticePeriod.rejected,  (s) => { s.noticePeriodGroupsLoading = false; });
        builder
            .addCase(fetchNotificationToRole.pending,   (s) => { s.notificationRolesLoading = true; })
            .addCase(fetchNotificationToRole.fulfilled, (s, a) => { s.notificationRolesLoading = false; s.notificationRoles = a.payload; })
            .addCase(fetchNotificationToRole.rejected,  (s) => { s.notificationRolesLoading = false; });
        builder
            .addCase(fetchNoticePeriodData.pending,   (s) => { s.noticePeriodDataLoading = true; s.noticePeriodDataError = null; })
            .addCase(fetchNoticePeriodData.fulfilled, (s, a) => { s.noticePeriodDataLoading = false; s.noticePeriodData = a.payload; })
            .addCase(fetchNoticePeriodData.rejected,  (s, a) => { s.noticePeriodDataLoading = false; s.noticePeriodDataError = a.payload; });
        [submitNoticePeriodRule, submitDeleteNoticePeriod].forEach((thunk) => {
            builder
                .addCase(thunk.pending,   (s) => { s.noticePeriodSaveLoading = true; s.noticePeriodSaveError = null; s.noticePeriodSaveResult = null; })
                .addCase(thunk.fulfilled, (s, a) => { s.noticePeriodSaveLoading = false; s.noticePeriodSaveResult = a.payload; })
                .addCase(thunk.rejected,  (s, a) => { s.noticePeriodSaveLoading = false; s.noticePeriodSaveError = a.payload; });
        });

        // Tax
        builder
            .addCase(fetchGroupsForTaxRule.pending,   (s) => { s.taxGroupsLoading = true; })
            .addCase(fetchGroupsForTaxRule.fulfilled, (s, a) => { s.taxGroupsLoading = false; s.taxGroups = a.payload; })
            .addCase(fetchGroupsForTaxRule.rejected,  (s) => { s.taxGroupsLoading = false; });
        builder
            .addCase(fetchStatesListForTax.pending,   (s) => { s.taxStatesLoading = true; })
            .addCase(fetchStatesListForTax.fulfilled, (s, a) => { s.taxStatesLoading = false; s.taxStates = a.payload; })
            .addCase(fetchStatesListForTax.rejected,  (s) => { s.taxStatesLoading = false; });
        builder
            .addCase(fetchProfessionalTaxData.pending,   (s) => { s.taxDataLoading = true; s.taxDataError = null; })
            .addCase(fetchProfessionalTaxData.fulfilled, (s, a) => { s.taxDataLoading = false; s.taxData = a.payload; })
            .addCase(fetchProfessionalTaxData.rejected,  (s, a) => { s.taxDataLoading = false; s.taxDataError = a.payload; });
        [submitTaxRule, submitDeleteTaxRule].forEach((thunk) => {
            builder
                .addCase(thunk.pending,   (s) => { s.taxSaveLoading = true; s.taxSaveError = null; s.taxSaveResult = null; })
                .addCase(thunk.fulfilled, (s, a) => { s.taxSaveLoading = false; s.taxSaveResult = a.payload; })
                .addCase(thunk.rejected,  (s, a) => { s.taxSaveLoading = false; s.taxSaveError = a.payload; });
        });

        // Travel
        builder
            .addCase(fetchGroupsForTravelRule.pending,   (s) => { s.travelGroupsLoading = true; })
            .addCase(fetchGroupsForTravelRule.fulfilled, (s, a) => { s.travelGroupsLoading = false; s.travelGroups = a.payload; })
            .addCase(fetchGroupsForTravelRule.rejected,  (s) => { s.travelGroupsLoading = false; });
        builder
            .addCase(fetchAllHRTravelExpRules.pending,   (s) => { s.travelDataLoading = true; s.travelDataError = null; })
            .addCase(fetchAllHRTravelExpRules.fulfilled, (s, a) => {
                s.travelDataLoading = false;
                s.travelData = a.payload?.TravelExpenseslst || [];
                s.travelGridType = a.payload?.GridType || s.travelGridType;
            })
            .addCase(fetchAllHRTravelExpRules.rejected,  (s, a) => { s.travelDataLoading = false; s.travelDataError = a.payload; });
        [submitTravelRule, submitDeleteTravelRule].forEach((thunk) => {
            builder
                .addCase(thunk.pending,   (s) => { s.travelSaveLoading = true; s.travelSaveError = null; s.travelSaveResult = null; })
                .addCase(thunk.fulfilled, (s, a) => { s.travelSaveLoading = false; s.travelSaveResult = a.payload; })
                .addCase(thunk.rejected,  (s, a) => { s.travelSaveLoading = false; s.travelSaveError = a.payload; });
        });
    },
});

export const {
    clearGroupSaveResult, clearCategorySaveResult, clearSalaryRuleSaveResult, clearNewSalaryHeadSaveResult,
    clearPFESISaveResult, clearHolidaySaveResult,
    clearELPaidLeaveSaveResult, clearLeaveExistCheck, clearGratuitySaveResult, clearAdvanceSaveResult,
    clearNoticePeriodSaveResult, clearTaxSaveResult, clearTravelSaveResult, resetHRRules,
} = hrRulesSlice.actions;

// ── Selectors ──────────────────────────────────────────────────────────────────
export const selectHRRules = (s) => s.hrRules;

export default hrRulesSlice.reducer;
